import { Controller } from "@hotwired/stimulus";
import { liquid, css, misc } from "./customization/category";
import { initToast } from "./customization/lib";
import { CustomizationSave } from "./customization/save";

// Connects to data-controller="site-builder--persistence"
export default class extends Controller {
  static targets = ["submit"];

  connect() {
    window.addEventListener("beforeunload", this.#eventHandler);
    initToast(this);

    window.addEventListener("pageshow", this.#resetOnBrowserBackNavigation);
    window.addEventListener('logo-section-blank', this.#validateFormFields);
  }

  disconnect() {
    window.removeEventListener("beforeunload", this.#eventHandler);
    window.removeEventListener("pageshow", this.#resetOnBrowserBackNavigation);
    window.removeEventListener('logo-section-blank', this.#validateFormFields);
  }

  updateButtonState() {
    const discardButton = document.querySelector('#discard-customizations');
    if (this.allStoresEmpty()) {
      this.submitTarget.classList.add("disabled");
      discardButton.disabled = true;
    } else {
      this.submitTarget.classList.remove("disabled");
      discardButton.disabled = false;
    }
  }

  save = async () => {
    this.hideContainerError();

    if (this.allStoresEmpty()) return;

    this.submitTarget.classList.add("loading");

    const siteId = document.querySelector("[data-site-builder-site]")?.dataset
      .siteBuilderSite;

    try {
      await new CustomizationSave(this.toast).perform(siteId);
    } catch (e) {
      console.error(e);
      this.submitTarget.classList.remove("loading");
      this.toast.error.show(
        "Site update failed",
        "You can retry or contact support if issue persists"
      );
    }
  };

  allStoresEmpty() {
    return css.isEmpty() && liquid.isEmpty() && misc.isEmpty();
  }

  #eventHandler = (e) => {
    if (this.allStoresEmpty()) return undefined;

    e.returnValue = true;
    return true;
  };

  #resetOnBrowserBackNavigation = (event) => {
    if (event.persisted) {
      /**
       * INFO: On Safari, when the user is viewing the Site Builder,
       * does some changes (but doesn't save them), and then moves to a
       * different page; an alert is shown to user for confirmation.
       * Now, after coming back to the Site Builder via the browser's
       * back button, Safari shows the same alert again.
       *
       * The solution to this is to remove the `beforeunload` event
       * that's attached above.
       */
      this.disconnect();

      /**
       * INFO: On Safari, when the user is viewing the Site Builder,
       * and moves to a different page; after coming back to the Site
       * Builder via the browser's back button, Safari shows the
       * previously cached page. This results in a mismatch of certain
       * elements.
       *
       * For eg: When a user is viewing Site Builder in mobile or tablet
       * view, then visits another URL, then comes back; the IFrame
       * remains in mobile (or tablet) view, but the device indicators
       * show that the desktop view is selected.
       *
       * We tried a few different solutions, but the one that we found
       * which works consistently across all browsers is to just reload
       * the page when the user hits the browser's back button.
       */
      window.location.reload();
    }
  }

  #validateFormFields = () => {
    this.submitTarget.classList.remove("loading");

    const wizardController = this.application.getControllerForElementAndIdentifier(document.querySelector('[data-controller="site-builder--wizard"]'), "site-builder--wizard");

    if (wizardController) {
      wizardController.closeAllPanel();
      wizardController.removeAllMenuSelection();

      // Select the "My Brand" tab and panel
      const brandTab = document.querySelector('#for-brand');
      if (brandTab) {
        brandTab.classList.add('selected');
        wizardController.render('customizable-brand');
      }
    }

    this.showContainerError();
  };

  #showOrHideContainerError(show = true) {
    const container = document.querySelector(".brand-fields-container.logo-container");
    const errorMessage = document.querySelector(".logo-error-message");

    if (show) {
      container.classList.add("with-error");
      errorMessage.classList.remove("hidden");
      container.scrollIntoView({ behavior: "smooth", block: "center" });
      container.focus();
    } else {
      container.classList.remove("with-error");
      errorMessage.classList.add("hidden");
    }
  }

  showContainerError() {
    this.#showOrHideContainerError(true);
  }

  hideContainerError() {
    this.#showOrHideContainerError(false);
  }
}
